import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBold,
  faItalic,
  faUnderline,
  faStrikethrough,
  faLink,
  faListUl,
  faListOl,
  faList,
  faSmile,
  faAt,
  faPaperPlane,
  faFont,
  faImage,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faArrowsUpDown,
  faCaretDown,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import {
  COMMON_EMOJIS,
  SKIN_TONES,
  applySkinTone,
  MESSAGE_FONTS,
  MESSAGE_FONT_SIZES,
  MESSAGE_LINE_SPACINGS,
  CHAT_IMAGE_ACCEPT,
  CHAT_IMAGE_ACCEPT_ANDROID,
  validateChatImageFile,
} from "../../../Utils/messageFormatting";
import {
  markupToHtml,
  htmlToMarkup,
  getEditorPlainText,
  saveEditorSelection,
  restoreEditorSelection,
  getSelectedText,
  isRangeInsideEditor,
  applyEditorCommand,
  insertTextAtSelection,
  insertLinkAtSelection,
  applyFontAndSizeToEditor,
  applyLineSpacingToEditor,
  getLineSpacingAtSelection,
  isSelectionInsideList,
  exitEmptyListItem,
  breakOutOfListAtCaret,
  insertLineBreakAtCaret,
} from "../../../Utils/messageWysiwyg";
import AddLinkModal from "./AddLinkModal";

const TooltipButton = ({
  label,
  className = "",
  tooltipPlacement = "bottom",
  disabled,
  children,
  type = "button",
  ...props
}) => (
  <button
    type={type}
    className={`message-composer-tooltip-host message-composer-tooltip-host--${tooltipPlacement} ${className}`.trim()}
    aria-label={label}
    disabled={disabled}
    {...props}
  >
    {children}
    <span className="message-composer-tooltip" role="tooltip">
      {label}
    </span>
  </button>
);

// Spacing a block uses when nothing has been applied to it.
const DEFAULT_LINE_SPACING = "1";

// Android needs two departures from the shared composer behaviour: its soft
// keyboard drops the newline the browser would insert on Enter, and its file
// picker only offers Camera/Photos for a broad "image/*" accept list.
const IS_ANDROID =
  typeof navigator !== "undefined" && /android/i.test(navigator.userAgent || "");

const MessageComposer = forwardRef(
  (
    {
      value,
      onChange,
      onSend,
      disabled = false,
      sending = false,
      placeholder = "Type your message",
      mentionMembers = [],
      editingMessageId = null,
      onCancelEdit,
      onFileUpload,
      uploadingFile = false,
    },
    ref
  ) => {
    const editorRef = useRef(null);
    const composerRef = useRef(null);
    const imageInputRef = useRef(null);
    const savedRangeRef = useRef(null);
    const lastMarkupRef = useRef("");
    const isInternalUpdateRef = useRef(false);

    const [showMentionPicker, setShowMentionPicker] = useState(false);
    const [mentionFilter, setMentionFilter] = useState("");
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkText, setLinkText] = useState("");
    const [linkUrl, setLinkUrl] = useState("");
    const [activeFormatMenu, setActiveFormatMenu] = useState(null);
    const [selectedFont, setSelectedFont] = useState(MESSAGE_FONTS[0]);
    const [selectedFontSize, setSelectedFontSize] = useState(16);
    const [activeLineSpacing, setActiveLineSpacing] = useState(DEFAULT_LINE_SPACING);
    const [isEmpty, setIsEmpty] = useState(true);
    const [isMobileComposer, setIsMobileComposer] = useState(
      () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
    );
    const [skinTone, setSkinTone] = useState(() => {
      try {
        return localStorage.getItem("fc-emoji-skin-tone") || "";
      } catch {
        return "";
      }
    });

    const isBusy = disabled || sending || uploadingFile;
    const showFontMenu = activeFormatMenu === "font";
    const showAlignMenu = activeFormatMenu === "align";
    const showLineSpacingMenu = activeFormatMenu === "lineSpacing";
    const showListMenu = activeFormatMenu === "list";
    const showEmojiPickerOpen = activeFormatMenu === "emoji";

    const adjustEditorHeight = useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.style.height = "auto";
      editor.style.height = `${Math.min(editor.scrollHeight, 200)}px`;
    }, []);

    const syncMarkupFromEditor = useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return;

      const markup = htmlToMarkup(editor);
      const plain = getEditorPlainText(editor).trim();
      setIsEmpty(!plain);

      isInternalUpdateRef.current = true;
      lastMarkupRef.current = markup;
      onChange(markup);

      const mentionMatch = getEditorPlainText(editor).match(/@([^\s@]*)$/);
      if (mentionMatch && mentionMembers.length > 0) {
        setShowMentionPicker(true);
        setMentionFilter(mentionMatch[1].toLowerCase());
      } else {
        setShowMentionPicker(false);
        setMentionFilter("");
      }

      adjustEditorHeight();
    }, [adjustEditorHeight, mentionMembers.length, onChange]);

    const setEditorFromMarkup = useCallback(
      (markup) => {
        const editor = editorRef.current;
        if (!editor) return;

        editor.innerHTML = markupToHtml(markup || "");
        lastMarkupRef.current = markup || "";
        setIsEmpty(!getEditorPlainText(editor).trim());
        // A cleared composer (e.g. after sending) starts back on the default.
        if (!markup) setActiveLineSpacing(DEFAULT_LINE_SPACING);
        adjustEditorHeight();
      },
      [adjustEditorHeight]
    );

    useImperativeHandle(ref, () => ({
      focus: () => editorRef.current?.focus(),
    }));

    useEffect(() => {
      if (isInternalUpdateRef.current) {
        isInternalUpdateRef.current = false;
        return;
      }
      if ((value || "") !== lastMarkupRef.current) {
        setEditorFromMarkup(value || "");
      }
    }, [value, setEditorFromMarkup]);

    const closeAllPopups = () => {
      setActiveFormatMenu(null);
      setShowMentionPicker(false);
    };

    const rememberSelection = () => {
      const range = saveEditorSelection();
      // Keep only a selection that lives inside the editor — a leftover page
      // selection (chat list text, another panel) would send formatting and
      // link insertion outside the composer.
      savedRangeRef.current =
        range && isRangeInsideEditor(editorRef.current, range) ? range : null;
    };

    const syncActiveLineSpacing = () => {
      const editor = editorRef.current;
      if (!editor) return;
      setActiveLineSpacing(
        getLineSpacingAtSelection(editor, savedRangeRef.current, DEFAULT_LINE_SPACING)
      );
    };

    const toggleFormatMenu = (menu) => {
      if (activeFormatMenu === menu) {
        setActiveFormatMenu(null);
        return;
      }
      rememberSelection();
      // Read the spacing under the caret so the menu opens with the current
      // option ticked.
      if (menu === "lineSpacing") syncActiveLineSpacing();
      setShowMentionPicker(false);
      setActiveFormatMenu(menu);
    };

    const runEditorCommand = (command, commandValue = null, { useSavedRange = false } = {}) => {
      const editor = editorRef.current;
      if (!editor) return;

      editor.focus();

      if (useSavedRange) {
        if (savedRangeRef.current) {
          restoreEditorSelection(savedRangeRef.current);
        }
      } else {
        const range = saveEditorSelection();
        if (range && editor.contains(range.commonAncestorContainer)) {
          savedRangeRef.current = range;
        } else if (savedRangeRef.current) {
          restoreEditorSelection(savedRangeRef.current);
        }
      }

      applyEditorCommand(editor, command, commandValue);
      syncMarkupFromEditor();
      closeAllPopups();
    };

    const openLinkModal = () => {
      if (disabled) return;
      rememberSelection();
      setLinkText(getSelectedText(editorRef.current));
      setLinkUrl("https://");
      setShowLinkModal(true);
    };

    const closeLinkModal = () => {
      setShowLinkModal(false);
      setLinkText("");
      setLinkUrl("");
    };

    const saveLink = () => {
      if (!linkUrl.trim()) return;
      const editor = editorRef.current;
      if (!editor) return;
      insertLinkAtSelection(editor, linkText, linkUrl, savedRangeRef.current);
      syncMarkupFromEditor();
      closeLinkModal();
    };

    const handleFormat = (type) => {
      if (type === "link") {
        openLinkModal();
        return;
      }

      switch (type) {
        case "bold":
          runEditorCommand("bold");
          break;
        case "italic":
          runEditorCommand("italic");
          break;
        case "underline":
          runEditorCommand("underline");
          break;
        case "strike":
          runEditorCommand("strikeThrough");
          break;
        case "alignLeft":
          runEditorCommand("justifyLeft", null, { useSavedRange: true });
          break;
        case "alignCenter":
          runEditorCommand("justifyCenter", null, { useSavedRange: true });
          break;
        case "alignRight":
          runEditorCommand("justifyRight", null, { useSavedRange: true });
          break;
        case "ordered":
          runEditorCommand("insertOrderedList", null, { useSavedRange: true });
          break;
        case "bullet":
          runEditorCommand("insertUnorderedList", null, { useSavedRange: true });
          break;
        default:
          break;
      }
    };

    const applyFontSelection = (font = selectedFont, size = selectedFontSize) => {
      const editor = editorRef.current;
      if (!editor) return;
      applyFontAndSizeToEditor(editor, savedRangeRef.current, font, size);
      syncMarkupFromEditor();
      setActiveFormatMenu(null);
    };

    const applyLineSpacingSelection = (lineHeight) => {
      const editor = editorRef.current;
      if (!editor) return;
      // Picking the option that is already ticked deselects it, putting the
      // block back on the default spacing.
      const isDeselect = lineHeight === activeLineSpacing;
      applyLineSpacingToEditor(editor, savedRangeRef.current, isDeselect ? null : lineHeight);
      setActiveLineSpacing(isDeselect ? DEFAULT_LINE_SPACING : lineHeight);
      syncMarkupFromEditor();
      setActiveFormatMenu(null);
    };

    const insertEmoji = (emoji) => {
      const editor = editorRef.current;
      if (!editor) return;
      restoreEditorSelection(savedRangeRef.current);
      insertTextAtSelection(editor, emoji);
      syncMarkupFromEditor();
      setActiveFormatMenu(null);
    };

    const changeSkinTone = (modifier) => {
      setSkinTone(modifier);
      try {
        localStorage.setItem("fc-emoji-skin-tone", modifier);
      } catch {
        /* ignore storage errors (private mode, etc.) */
      }
    };

    const insertMention = (name) => {
      const editor = editorRef.current;
      if (!editor) return;

      editor.focus();
      const plain = getEditorPlainText(editor);
      const mentionTrigger = plain.match(/@([^\s@]*)$/);

      if (mentionTrigger) {
        const charsToDelete = mentionTrigger[0].length;
        for (let i = 0; i < charsToDelete; i += 1) {
          document.execCommand("delete", false, null);
        }
      }

      insertTextAtSelection(editor, `@${name} `);
      syncMarkupFromEditor();
      setShowMentionPicker(false);
      setMentionFilter("");
    };

    const handleImageChange = async (e) => {
      const file = e.target.files?.[0];
      if (imageInputRef.current) imageInputRef.current.value = "";

      if (!file || !onFileUpload) return;

      const validation = validateChatImageFile(file);
      if (!validation.valid) {
        toast.error(validation.message);
        return;
      }

      try {
        await onFileUpload(file);
      } catch (error) {
        console.error("Image upload failed:", error);
        toast.error(error?.message || "Failed to upload image. Please try again.");
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        if (isSelectionInsideList(editorRef.current)) {
          // Shift+Enter breaks out of the list to a plain, left-aligned line.
          // Plain Enter does the same only on an empty item; on a non-empty
          // item it falls through so the browser makes the next bullet.
          const exited = e.shiftKey
            ? breakOutOfListAtCaret(editorRef.current)
            : exitEmptyListItem(editorRef.current);
          if (exited) {
            e.preventDefault();
            syncMarkupFromEditor();
          }
          return;
        }
        // On mobile the keyboard's return key is the only way to start a new
        // line (no Shift+Enter), so it never sends — the send button does.
        if (e.shiftKey || isMobileComposer) return;
        e.preventDefault();
        if (!isEmpty && !sending && !disabled && !uploadingFile) {
          onSend(e);
        }
        return;
      }

      if (e.key === "Escape") {
        closeAllPopups();
      }
    };

    const handleEditorInput = () => {
      syncMarkupFromEditor();
    };

    const handleEditorPaste = (e) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      insertTextAtSelection(editorRef.current, text);
      syncMarkupFromEditor();
    };

    const filteredMentions = mentionMembers.filter((m) =>
      m.name.toLowerCase().includes(mentionFilter)
    );

    useEffect(() => {
      const mq = window.matchMedia("(max-width: 768px)");
      const handleChange = (e) => setIsMobileComposer(e.matches);
      setIsMobileComposer(mq.matches);
      mq.addEventListener("change", handleChange);
      return () => mq.removeEventListener("change", handleChange);
    }, []);

    // Enter on Android arrives as a beforeinput, and its default action is
    // unreliable while the keyboard is composing — insert the break ourselves.
    useEffect(() => {
      const editor = editorRef.current;
      if (!editor || !IS_ANDROID || !isMobileComposer) return undefined;

      const handleBeforeInput = (e) => {
        if (e.inputType !== "insertParagraph" && e.inputType !== "insertLineBreak") return;
        // Inside a list the browser still has to create the next bullet.
        if (isSelectionInsideList(editor)) return;
        e.preventDefault();
        if (insertLineBreakAtCaret(editor)) syncMarkupFromEditor();
      };

      editor.addEventListener("beforeinput", handleBeforeInput);
      return () => editor.removeEventListener("beforeinput", handleBeforeInput);
    }, [isMobileComposer, syncMarkupFromEditor]);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (composerRef.current && !composerRef.current.contains(e.target)) {
          closeAllPopups();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const formatBtn = (icon, label, type, extraClass = "") => (
      <TooltipButton
        key={type}
        label={label}
        className={`message-composer-format-btn ${extraClass}`.trim()}
        tooltipPlacement="bottom"
        disabled={isBusy}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => handleFormat(type)}
      >
        <FontAwesomeIcon icon={icon} />
      </TooltipButton>
    );

    return (
      <div className="message-composer" ref={composerRef}>
        {editingMessageId && onCancelEdit && (
          <div className="message-composer-edit-banner">
            <span>Editing message</span>
            <button type="button" onClick={onCancelEdit} className="message-composer-edit-cancel">
              Cancel
            </button>
          </div>
        )}

        <div className="message-composer-box">
          {/* Desktop only: on mobile the return key already starts a new line
              and there is no Shift to press. */}
          {!isMobileComposer && (
            <div className="message-composer-hint-bar">
              <p className="message-composer-hint">
                Press <kbd>Shift</kbd>+<kbd>Enter</kbd> To go to a new line
              </p>
            </div>
          )}

          <div className="message-composer-toolbar message-composer-toolbar--format">
            {mentionMembers.length > 0 && (
              <div className="message-composer-action-wrap">
                <TooltipButton
                  label="Mention someone"
                  className="message-composer-format-btn"
                  tooltipPlacement="bottom-start"
                  disabled={isBusy}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    insertTextAtSelection(editorRef.current, "@");
                    syncMarkupFromEditor();
                    setActiveFormatMenu(null);
                    setShowMentionPicker(true);
                    setMentionFilter("");
                  }}
                >
                  <FontAwesomeIcon icon={faAt} />
                </TooltipButton>
                {showMentionPicker && filteredMentions.length > 0 && (
                  <div className="message-composer-popup message-composer-mention-picker">
                    {filteredMentions.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        className="message-composer-mention-item"
                        onClick={() => insertMention(m.name)}
                      >
                        <span
                          className="message-composer-mention-dot"
                          style={{ backgroundColor: m.hexCode || "#6b7280" }}
                        />
                        {m.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="message-composer-action-wrap">
              <TooltipButton
                label="Font and Size"
                className={`message-composer-format-btn message-composer-list-btn${showFontMenu ? " active" : ""}`}
                tooltipPlacement="bottom"
                disabled={isBusy}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => toggleFormatMenu("font")}
              >
                <FontAwesomeIcon icon={faFont} />
                <FontAwesomeIcon icon={faCaretDown} className="message-composer-caret" />
              </TooltipButton>
              {showFontMenu && (
                <div className="message-composer-popup message-composer-font-menu">
                  <label className="message-composer-menu-label">
                    Font
                    <select
                      value={selectedFont}
                      onChange={(e) => setSelectedFont(e.target.value)}
                    >
                      {MESSAGE_FONTS.map((font) => (
                        <option key={font} value={font} style={{ fontFamily: font }}>
                          {font}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="message-composer-menu-label">
                    Size
                    <select
                      value={selectedFontSize}
                      onChange={(e) => setSelectedFontSize(Number(e.target.value))}
                    >
                      {MESSAGE_FONT_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="message-composer-menu-apply"
                    onClick={() => applyFontSelection()}
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            {formatBtn(faBold, "Bold", "bold")}
            {formatBtn(faItalic, "Italic", "italic")}
            {formatBtn(faUnderline, "Underline", "underline")}
            {formatBtn(faStrikethrough, "Strikethrough", "strike")}

            {formatBtn(faLink, "Link", "link")}

            <div className="message-composer-action-wrap">
              <TooltipButton
                label="Insert Picture"
                className="message-composer-format-btn"
                tooltipPlacement="bottom"
                disabled={isBusy || !onFileUpload}
                onClick={() => imageInputRef.current?.click()}
              >
                <FontAwesomeIcon icon={faImage} />
              </TooltipButton>
              <input
                ref={imageInputRef}
                type="file"
                hidden
                accept={IS_ANDROID ? CHAT_IMAGE_ACCEPT_ANDROID : CHAT_IMAGE_ACCEPT}
                onChange={handleImageChange}
                disabled={isBusy}
              />
            </div>

            <div className="message-composer-action-wrap">
              <TooltipButton
                label="Insert Emojis"
                className={`message-composer-format-btn${showEmojiPickerOpen ? " active" : ""}`}
                tooltipPlacement="bottom"
                disabled={isBusy}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => toggleFormatMenu("emoji")}
              >
                <FontAwesomeIcon icon={faSmile} />
              </TooltipButton>
              {showEmojiPickerOpen && (
                <div className="message-composer-popup message-composer-emoji-popup">
                  <div className="message-composer-emoji-grid">
                    {COMMON_EMOJIS.map((emoji) => {
                      const display = applySkinTone(emoji, skinTone);
                      return (
                        <button
                          key={emoji}
                          type="button"
                          className="message-composer-emoji-btn"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => insertEmoji(display)}
                          aria-label={`Insert ${display}`}
                        >
                          {display}
                        </button>
                      );
                    })}
                  </div>
                  <div className="message-composer-emoji-skintone">
                    <span className="message-composer-emoji-skintone-label">Skin tone</span>
                    <div className="message-composer-emoji-skintone-swatches">
                      {SKIN_TONES.map((tone) => (
                        <button
                          key={tone.key}
                          type="button"
                          className={`message-composer-emoji-skintone-btn${
                            skinTone === tone.modifier ? " active" : ""
                          }`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => changeSkinTone(tone.modifier)}
                          aria-label={`${tone.label} skin tone`}
                          aria-pressed={skinTone === tone.modifier}
                          title={tone.label}
                        >
                          {`✋${tone.modifier}`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="message-composer-action-wrap">
              <TooltipButton
                label="Align"
                className={`message-composer-format-btn message-composer-list-btn${showAlignMenu ? " active" : ""}`}
                tooltipPlacement="bottom"
                disabled={isBusy}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => toggleFormatMenu("align")}
              >
                <FontAwesomeIcon icon={faAlignCenter} />
                <FontAwesomeIcon icon={faCaretDown} className="message-composer-caret" />
              </TooltipButton>
              {showAlignMenu && (
                <div className="message-composer-popup message-composer-list-menu">
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleFormat("alignLeft")}
                  >
                    <FontAwesomeIcon icon={faAlignLeft} />
                    Left
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleFormat("alignCenter")}
                  >
                    <FontAwesomeIcon icon={faAlignCenter} />
                    Center
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleFormat("alignRight")}
                  >
                    <FontAwesomeIcon icon={faAlignRight} />
                    Right
                  </button>
                </div>
              )}
            </div>

            <div className="message-composer-action-wrap">
              <TooltipButton
                label="Line Spacing"
                className={`message-composer-format-btn message-composer-list-btn${
                  showLineSpacingMenu || activeLineSpacing !== DEFAULT_LINE_SPACING ? " active" : ""
                }`}
                tooltipPlacement="bottom"
                disabled={isBusy}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => toggleFormatMenu("lineSpacing")}
              >
                <FontAwesomeIcon icon={faArrowsUpDown} />
                <FontAwesomeIcon icon={faCaretDown} className="message-composer-caret" />
              </TooltipButton>
              {showLineSpacingMenu && (
                <div
                  className="message-composer-popup message-composer-list-menu message-composer-checkable-menu"
                  role="menu"
                >
                  {MESSAGE_LINE_SPACINGS.map((option) => {
                    const isActive = option.value === activeLineSpacing;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="menuitemradio"
                        aria-checked={isActive}
                        className={isActive ? "is-selected" : ""}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyLineSpacingSelection(option.value)}
                      >
                        <span className="message-composer-menu-check">
                          {isActive && <FontAwesomeIcon icon={faCheck} />}
                        </span>
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="message-composer-action-wrap">
              <TooltipButton
                label="List dropdown for bullet & number"
                className={`message-composer-format-btn message-composer-list-btn${showListMenu ? " active" : ""}`}
                tooltipPlacement="bottom"
                disabled={isBusy}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => toggleFormatMenu("list")}
              >
                <FontAwesomeIcon icon={faList} />
                <FontAwesomeIcon icon={faCaretDown} className="message-composer-caret" />
              </TooltipButton>
              {showListMenu && (
                <div className="message-composer-popup message-composer-list-menu">
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleFormat("bullet")}
                  >
                    <FontAwesomeIcon icon={faListUl} />
                    Bulleted list
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleFormat("ordered")}
                  >
                    <FontAwesomeIcon icon={faListOl} />
                    Numbered list
                  </button>
                </div>
              )}
            </div>

          </div>

          <div
            ref={editorRef}
            className={`message-composer-input message-composer-editor${isEmpty ? " is-empty" : ""}`}
            contentEditable={!(isBusy || sending)}
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Message input"
            data-placeholder={placeholder}
            onInput={handleEditorInput}
            onKeyDown={handleKeyDown}
            onPaste={handleEditorPaste}
          />

          <div className="message-composer-toolbar message-composer-toolbar--actions">
            <TooltipButton
              type="submit"
              label="Send message"
              className="message-composer-send-btn"
              tooltipPlacement="top"
              disabled={isEmpty || isBusy}
            >
              {sending || uploadingFile ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FontAwesomeIcon icon={faPaperPlane} />
              )}
            </TooltipButton>
          </div>
        </div>

        <AddLinkModal
          isOpen={showLinkModal}
          text={linkText}
          url={linkUrl}
          onTextChange={setLinkText}
          onUrlChange={setLinkUrl}
          onSave={saveLink}
          onClose={closeLinkModal}
        />
      </div>
    );
  }
);

MessageComposer.displayName = "MessageComposer";

export default MessageComposer;
